import React, { useState, useEffect } from 'react';
import { 
  FaChartLine, FaArrowLeft, FaCheckCircle, FaUsers, FaTimes, FaHistory,
  FaArrowRight, FaLightbulb, FaBolt, FaRocket, FaShieldAlt, FaWallet,
  FaBuilding, FaIndustry, FaTrendingUp, FaGlobe
} from 'react-icons/fa';
import { 
  MdOutlineCurrencyExchange, MdSpeed, MdTimeline, 
  MdDashboard, MdBarChart, MdBusiness, MdAttachMoney
} from 'react-icons/md';
import { IoWallet, IoFlash, IoTrendingUp, IoCalculator, IoBusiness } from 'react-icons/io5';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';
import HotProductSection from './HotProductSection';

const CompanyShares = () => {
  // State management (keeping original state variables for functionality)
  const [currentView, setCurrentView] = useState('main');
  const [depositAmount, setDepositAmount] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [products, setProducts] = useState([]);
  const [toastNotification, setToastNotification] = useState(null);
  const [investmentSuccess, setInvestmentSuccess] = useState(null);
  const { userDetails, token, updateUserDetails, isLoading } = useAuth();

  // Payment processing states
  const [showPaymentLoader, setShowPaymentLoader] = useState(false);
  const [paymentLoaderMessage, setPaymentLoaderMessage] = useState('');
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [transactionCode, setTransactionCode] = useState('');
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [hotProducts, setHotProducts] = useState([]);
  const [showInvestmentDialog, setShowInvestmentDialog] = useState(false);

  useEffect(() => {
    if (!isLoading && userDetails?.mobile) {
      setPhoneNumber(userDetails.mobile);
    }
    if (!isLoading && userDetails?.email) {
      fetchAvailableProducts();
    }
  }, [userDetails, isLoading]);

  const formatPhoneNumber = (number) => {
    if (number.startsWith('07') || number.startsWith('01')) {
      return '254' + number.slice(1);
    }
    return number;
  };

  // Data refresh functions
  const refreshUserData = async () => {
    try {
      const userResponse = await axios.get(`https://api.globallequityfinance.com/api/v1/auth/${userDetails.email}`, {
        headers: {
          'Authorization': token
        }
      });
      updateUserDetails(userResponse.data);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setToastNotification({ message: 'Failed to refresh user data', type: 'error' });
    }
  };

  const refreshProducts = async () => {
    try {
      const response = await axios.get(`https://api.globallequityfinance.com/api/products/available/${userDetails.email}`);
      if (response.data && Array.isArray(response.data)) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  const refreshAllData = async () => {
    await Promise.all([
      refreshUserData(),
      refreshProducts()
    ]);
  };

  useEffect(() => {
    if (currentView === 'main') {
      refreshAllData();
    }
  }, [currentView]);

  const handlePaymentSuccess = async () => {
    await refreshAllData();
    setCurrentView('paymentSuccess');
  };

  const fetchAvailableProducts = async () => {
  try {
    const [productsResponse, hotProductsResponse] = await Promise.all([
      axios.get(`https://api.globallequityfinance.com/api/products/available/${userDetails.email}`),
      axios.get('https://api.globallequityfinance.com/api/products/hot')
    ]);
    
    if (productsResponse.data && Array.isArray(productsResponse.data)) {
      setProducts(productsResponse.data.filter(p => p.productType === 'NORMAL'));
    }
    
    if (hotProductsResponse.data && Array.isArray(hotProductsResponse.data)) {
      setHotProducts(hotProductsResponse.data); // Store all hot products
    }
  } catch (error) {
    console.error('Error fetching products:', error);
    setToastNotification({ message: 'Failed to fetch company shares. Please try again.', type: 'error' });
  }
};

  const handleInvest = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setShowInvestmentDialog(true);
  };

  const handleCloseInvestmentDialog = () => {
    setShowInvestmentDialog(false);
    setSelectedProduct(null);
    setQuantity(1);
  };

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);
  };

  const calculateTotalBalance = () => {
    const wallet = Number(userDetails.wallet) || 0;
    const invitationIncome = Number(userDetails.invitationIncome) || 0;
    return (wallet + invitationIncome).toLocaleString();
  };

  const handleInvestNow = async () => {
    try {
      const response = await axios.post('https://api.globallequityfinance.com/api/investments/invest', null, {
        params: {
          userEmail: userDetails.email,
          productId: selectedProduct.id,
          quantity: quantity
        }
      });
      
      if (response.status === 200) {
        await refreshAllData();
        setShowInvestmentDialog(false);
        setInvestmentSuccess(response.data);
        setCurrentView('investmentSuccess');
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setToastNotification({ message: error.response.data, type: 'error' });
        setCurrentView('deposit');
        setDepositAmount((selectedProduct.price * quantity).toString());
      } else {
        console.error('Error making investment:', error);
        setToastNotification({ message: 'Error, please select another package!', type: 'error' });
      }
    }
  };

  // Payment processing functions (keeping original functionality)
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setShowPaymentLoader(true);
    setPaymentLoaderMessage('Please check your phone to complete payment.');

    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
    const requestData = {
      PhoneNumber: formattedPhoneNumber,
      Amount: parseInt(depositAmount),
    };

    try {
      await axios.post('https://api.globallequityfinance.com/mobile-money/stk-transaction-request', requestData, {
        headers: {
          'Authorization': token
        }
      });
      
      setTimeout(() => {
        setPaymentLoaderMessage('Processing transaction...');
      }, 20000);

      setTimeout(() => {
        checkPaymentStatus(formattedPhoneNumber);
      }, 27000);
    } catch (error) {
      console.error(error);
      setShowPaymentLoader(false);
      setToastNotification({ message: 'Failed to initiate payment. Please try again.', type: 'error' });
    }
  };

  const checkPaymentStatus = async (phoneNumber) => {
    try {
      const response = await axios.get(`https://api.globallequityfinance.com/mobile-money/transactions/check-payment/${phoneNumber}`, {
        headers: {
          'Authorization': token
        }
      });
  
      if (response.data.paymentFound) {
        await processTransaction(phoneNumber);
        setPaymentSuccess(true);
        setShowPaymentLoader(false);
        setToastNotification({ message: 'Payment successful!', type: 'success' });
        setCurrentView('paymentSuccess');
      } else {
        setShowPaymentLoader(false);
        setShowTransactionForm(true);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setShowPaymentLoader(false);
      setToastNotification({ message: 'Failed to check payment status. Please try again.', type: 'error' });
    }
  };

  const processTransaction = async (phoneNumber) => {
    try {
      const response = await axios.post('https://api.globallequityfinance.com/mobile-money/users/activate', {
        phoneNumber: phoneNumber,
        email: userDetails.email
      }, {
        headers: {
          'Authorization': token
        }
      });
  
      if (response.data.message) {
        setIsActivated(true);
        await refreshAllData();
      }
    } catch (error) {
      console.error('Error processing transaction:', error);
      throw error;
    }
  };

  const handleTransactionCodeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingPayment(true);
  
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
  
    try {
      const verifyResponse = await axios.post('https://api.globallequityfinance.com/mobile-money/transactions/verify', {
        phoneNumber: formattedPhoneNumber,
        transactionCode: transactionCode
      }, {
        headers: {
          'Authorization': token
        }
      });
  
      if (verifyResponse.data.verified) {
        await processTransaction(formattedPhoneNumber);
        await refreshAllData();
        setPaymentSuccess(true);
        setCurrentView('paymentSuccess');
        setToastNotification({ message: 'Payment verified successfully!', type: 'success' });
      } else {
        setToastNotification({ message: 'Invalid transaction code. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Error verifying transaction:', error);
      setToastNotification({ message: 'Failed to verify transaction. Please try again.', type: 'error' });
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  // Add this to your component logic
const handleCloseToast = () => {
  // First set the exiting state
  setToastNotification(prev => ({
    ...prev,
    isExiting: true
  }));
  
  // Then remove toast after animation completes
  setTimeout(() => {
    setToastNotification(null);
  }, 400); // Match the animation duration
};

// Also add auto-dismiss functionality if desired
useEffect(() => {
  if (toastNotification && !toastNotification.isExiting) {
    const timer = setTimeout(() => {
      handleCloseToast();
    }, 5000); // Auto-dismiss after 5 seconds
    
    return () => clearTimeout(timer);
  }
}, [toastNotification]);

  // Helper to get icon based on company name
  const getCompanyIcon = (name) => {
    name = name.toLowerCase();
    if (name.includes('tech') || name.includes('digital')) return <MdDashboard />;
    if (name.includes('bank') || name.includes('finance')) return <MdAttachMoney />;
    if (name.includes('energy') || name.includes('oil')) return <FaIndustry />;
    if (name.includes('real estate') || name.includes('property')) return <FaBuilding />;
    if (name.includes('retail') || name.includes('consumer')) return <FaUsers />;
    if (name.includes('pharma') || name.includes('health')) return <FaShieldAlt />;
    return <IoBusiness />;
  };

  // Helper to get color based on company sector
  const getCompanyColor = (name) => {
    name = name.toLowerCase();
    if (name.includes('tech') || name.includes('digital')) return '#2563EB';
    if (name.includes('bank') || name.includes('finance')) return '#059669';
    if (name.includes('energy') || name.includes('oil')) return '#DC2626';
    if (name.includes('real estate') || name.includes('property')) return '#7C3AED';
    if (name.includes('retail') || name.includes('consumer')) return '#EA580C';
    if (name.includes('pharma') || name.includes('health')) return '#0891B2';
    return '#e74c3c';
  };

  return (
    <div className="cs-shares-container">
      {currentView === 'main' && (
  <>
    <div className="cs-shares-header">
      <div className="cs-header-content">
        <h1 className="cs-shares-title">Company Shares</h1>
        <p className="cs-shares-subtitle">Invest in leading corporations</p>
      </div>
      <div className="cs-header-actions">
        <button className="cs-fund-button" onClick={() => setCurrentView('deposit')}>
          <FaWallet className="cs-fund-icon" />
          <span>Fund Account</span>
        </button>
      </div>
    </div>
    
    {hotProducts.length > 0 && (
      <HotProductSection products={hotProducts} onInvest={handleInvest} />
    )}
          
          <div className="cs-shares-grid">
            {products.map((product, index) => {
              const companyColor = getCompanyColor(product.name);
              return (
                <div 
                  key={index} 
                  className="cs-share-card"
                >
                  <div className="cs-card-header">
                    <div className="cs-company-image">
                      <img 
                        src={`https://api.globallequityfinance.com${product.imageUrl}`} 
                        alt={product.name} 
                        className="cs-company-logo" 
                      />
                    </div>
                    <div className="cs-company-info">
                      <h3 className="cs-company-name">{product.name}</h3>
                      <div className="cs-company-icon" style={{ color: companyColor }}>
                        {getCompanyIcon(product.name)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="cs-share-metrics">
                    <div className="cs-metric-row">
                      <div className="cs-metric">
                        <span className="cs-metric-label">Duration</span>
                        <span className="cs-metric-value">{product.cycle} days</span>
                      </div>
                      <div className="cs-metric">
                        <span className="cs-metric-label">Daily Return</span>
                        <span className="cs-metric-value">KES {product.dailyIncome}</span>
                      </div>
                    </div>
                    
                    <div className="cs-metric-row">
                      <div className="cs-metric">
                        <span className="cs-metric-label">Total Return</span>
                        <span className="cs-metric-value">KES {product.totalIncome}</span>
                      </div>
                      <div className="cs-metric">
                        <span className="cs-metric-label">Available</span>
                        <span className="cs-metric-value">{product.available} shares</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="cs-share-footer">
                    <div className="cs-share-price">
                      <span className="cs-price-label">Share Price</span>
                      <span className="cs-price-value">KES {product.price}</span>
                    </div>
                    <button 
                      className={`cs-invest-button ${product.available < 1 ? 'cs-disabled' : ''}`}
                      onClick={() => product.available >= 1 && handleInvest(product)}
                      disabled={product.available < 1}
                      style={{ backgroundColor: product.available >= 1 ? companyColor : '#64748b' }}
                    >
                      {product.available < 1 ? 'Sold Out' : 'Invest'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      
      {currentView === 'investmentSuccess' && (
        <div className="cs-success-view">
          <div className="cs-success-animation">
            <FaCheckCircle className="cs-success-icon" />
          </div>
          
          <h2 className="cs-success-title">Investment Successful!</h2>
          <p className="cs-success-subtitle">Your shares have been purchased</p>
          
          <div className="cs-success-card">
            <div className="cs-success-header">
              <div className="cs-company-icon-small" style={{ color: getCompanyColor(investmentSuccess.productName) }}>
                {getCompanyIcon(investmentSuccess.productName)}
              </div>
              <h3 className="cs-success-company-name">{investmentSuccess.productName}</h3>
            </div>
            
            <div className="cs-success-details">
              <div className="cs-success-detail">
                <span className="cs-success-label">Investment</span>
                <span className="cs-success-value">KES {investmentSuccess.investedAmount.toLocaleString()}</span>
              </div>
              
              <div className="cs-success-detail">
                <span className="cs-success-label">Shares</span>
                <span className="cs-success-value">{quantity} share{quantity > 1 ? 's' : ''}</span>
              </div>
              
              <div className="cs-success-detail">
                <span className="cs-success-label">Daily Return</span>
                <span className="cs-success-value">KES {investmentSuccess.dailyIncome.toLocaleString()}</span>
              </div>
              
              <div className="cs-success-detail">
                <span className="cs-success-label">Total Return</span>
                <span className="cs-success-value">KES {investmentSuccess.totalIncome.toLocaleString()}</span>
              </div>
              
              <div className="cs-success-detail">
                <span className="cs-success-label">Maturity Date</span>
                <span className="cs-success-value">{investmentSuccess.validUntil.split('T')[0]}</span>
              </div>
            </div>
          </div>
          
          <div className="cs-info-card">
            <FaLightbulb className="cs-info-icon" />
            <h4 className="cs-info-title">How to Track Your Returns</h4>
            <p className="cs-info-text">Your returns are calculated daily and added to your wallet. Monitor your portfolio performance in the Dashboard.</p>
          </div>
          
          <div className="cs-success-actions">
            <button 
              className="cs-view-button"
              onClick={() => window.location.href = '/history'}
            >
              <MdDashboard className="cs-button-icon" />
              View Portfolio
            </button>
            
            <button 
              className="cs-home-button"
              onClick={() => setCurrentView('main')}
            >
              Back to Shares
            </button>
          </div>
        </div>
      )}
      
      {currentView === 'deposit' && (
        <div className="cs-form-view">
          <div className="cs-form-header">
            <button 
              className="cs-back-button"
              onClick={() => setCurrentView('main')}
            >
              <FaArrowLeft />
            </button>
            <h2 className="cs-form-title">Fund Your Account</h2>
          </div>
          
          <div className="cs-form-container">
            <div className="cs-form-content">
              <div className="cs-input-group">
                <label className="cs-input-label">Amount (KES)</label>
                <div className="cs-input-wrapper">
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDepositAmount(value);
                      if (toastNotification) {
                        setToastNotification(null);
                      }
                    }}
                    className="cs-input-field"
                    placeholder="Enter amount (Minimum: KES 300)"
                  />
                </div>
                <p className="cs-input-hint">Minimum deposit: KES 300</p>
              </div>
              
              <button 
                className={`cs-submit-button ${!depositAmount || parseFloat(depositAmount) < 300 ? 'cs-disabled' : ''}`}
                onClick={() => {
                  if (!depositAmount || parseFloat(depositAmount) < 300) {
                    setToastNotification({ 
                      message: 'Minimum deposit amount is KES 300', 
                      type: 'error' 
                    });
                    return;
                  }
                  setCurrentView('paymentProcess');
                }}
                disabled={!depositAmount || parseFloat(depositAmount) < 300}
              >
                Continue to Payment
                <FaArrowRight className="cs-button-icon-right" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {currentView === 'paymentProcess' && (
        <div className="cs-form-view">
          <div className="cs-form-header">
            <button 
              className="cs-back-button"
              onClick={() => setCurrentView('deposit')}
            >
              <FaArrowLeft />
            </button>
            <h2 className="cs-form-title">Process Payment</h2>
          </div>
          
          {!showPaymentLoader && !showTransactionForm && (
            <div className="cs-form-container">
              <div className="cs-payment-summary">
                <div className="cs-summary-item">
                  <span className="cs-summary-label">Amount</span>
                  <span className="cs-summary-value">KES {depositAmount}</span>
                </div>
                
                <div className="cs-summary-item">
                  <span className="cs-summary-label">Payment Method</span>
                  <span className="cs-summary-value">Mobile Money</span>
                </div>
                
                <div className="cs-summary-item">
                  <span className="cs-summary-label">Phone Number</span>
                  <span className="cs-summary-value">{phoneNumber}</span>
                </div>
              </div>
              
              <div className="cs-payment-note">
                <p>A payment prompt will be sent to your phone. Please enter your PIN to complete the transaction.</p>
              </div>
              
              <button 
                className="cs-submit-button"
                onClick={handlePaymentSubmit}
              >
                Proceed with Payment
                <FaArrowRight className="cs-button-icon-right" />
              </button>
            </div>
          )}
          
          {showPaymentLoader && (
            <div className="cs-loader-container">
              <div className="cs-loader"></div>
              <h3 className="cs-loader-title">Processing Your Payment</h3>
              <p className="cs-loader-message">{paymentLoaderMessage}</p>
            </div>
          )}
          
          {showTransactionForm && (
            <div className="cs-form-container">
              <div className="cs-payment-error">
                <FaTimes className="cs-error-icon" />
                <h3 className="cs-error-title">Payment Not Detected</h3>
                <p className="cs-error-message">We couldn't verify your payment. Please try again or contact support if you've already made the payment.</p>
              </div>
              
              <button 
                className="cs-retry-button"
                onClick={() => setCurrentView('deposit')}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}
      
      {currentView === 'paymentSuccess' && (
        <div className="cs-success-view">
          <div className="cs-success-animation">
            <FaCheckCircle className="cs-success-icon" />
          </div>
          
          <h2 className="cs-success-title">Payment Complete!</h2>
          <p className="cs-success-subtitle">Your account has been funded successfully</p>
          
          <div className="cs-success-card cs-payment-success-card">
            <div className="cs-success-details">
              <div className="cs-success-detail">
                <span className="cs-success-label">Amount</span>
                <span className="cs-success-value">KES {depositAmount}</span>
              </div>
              
              <div className="cs-success-detail">
                <span className="cs-success-label">Status</span>
                <span className="cs-success-value cs-status-complete">Completed</span>
              </div>
              
              <div className="cs-success-detail">
                <span className="cs-success-label">Available Balance</span>
                <span className="cs-success-value cs-updated-balance">KES {calculateTotalBalance()}</span>
              </div>
            </div>
          </div>
          
          <div className="cs-success-actions">
            <button 
              className="cs-view-button"
              onClick={async () => {
                await refreshAllData();
                setCurrentView('main');
                setDepositAmount('');
                setPaymentSuccess(false);
                setShowTransactionForm(false);
              }}
            >
              Continue Shopping
              <FaArrowRight className="cs-button-icon-right" />
            </button>
          </div>
        </div>
      )}
      
      {showInvestmentDialog && selectedProduct && (
        <div className="cs-modal-overlay">
          <div className="cs-modal-container">
            <div className="cs-modal-header">
              <h3 className="cs-modal-title">Purchase Shares</h3>
              <button className="cs-modal-close" onClick={handleCloseInvestmentDialog}>
                <FaTimes />
              </button>
            </div>
            
            <div className="cs-modal-content">
              <div className="cs-company-highlight">
                <div className="cs-highlight-icon" style={{ color: getCompanyColor(selectedProduct.name) }}>
                  {getCompanyIcon(selectedProduct.name)}
                </div>
                <h4 className="cs-highlight-name">{selectedProduct.name}</h4>
              </div>
              
              <div className="cs-quantity-selector">
                <h4 className="cs-selector-label">Select Quantity</h4>
                <div className="cs-selector-options">
                  {[...Array(Math.min(selectedProduct.available, 5))].map((_, index) => (
                    <button
                      key={index + 1}
                      className={`cs-quantity-option ${quantity === index + 1 ? 'active' : ''}`}
                      onClick={() => handleQuantityChange(index + 1)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
              
              <button 
                className="cs-modal-submit"
                onClick={handleInvestNow}
              >
                Purchase Shares
                <FaBolt className="cs-button-icon-right" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <BottomNavigation />
      
      {toastNotification && (
        <div className={`toast-notification ${toastNotification.isExiting ? 'exiting' : ''}`}>
          <div className="toast-icon">
            {toastNotification.type === 'success' ? (
              <FaCheckCircle className="success-icon" />
            ) : (
              <FaTimes className="error-icon" />
            )}
          </div>
          <div className="toast-content">
            <div className="toast-title">
              {toastNotification.type === 'success' ? 'Success' : 'Error'}
            </div>
            <div className="toast-message">{toastNotification.message}</div>
          </div>
          <button className="toast-close" onClick={() => handleCloseToast()}>
            <FaTimes className="close-icon" />
          </button>
          <style jsx>{`
            .toast-notification {
              position: fixed;
              bottom: 24px;
              right: 24px;
              display: flex;
              align-items: center;
              padding: 12px 16px;
              border-radius: 8px;
              box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
              max-width: 350px;
              background-color: ${toastNotification.type === 'success' ? '#ecfdf5' : '#fef2f2'};
              border-left: 4px solid ${toastNotification.type === 'success' ? '#10B981' : '#EF4444'};
              z-index: 9999;
              animation: slideIn 0.4s ease-out forwards;
              transition: all 0.3s ease-in-out;
            }
            
            .toast-notification.exiting {
              animation: slideOut 0.4s ease-out forwards;
            }
            
            .toast-icon {
              padding: 8px;
              border-radius: 50%;
              background-color: ${toastNotification.type === 'success' 
                ? 'rgba(16, 185, 129, 0.1)'
                : 'rgba(239, 68, 68, 0.1)'};
             margin-right: 12px;
           }
           
           .success-icon {
             color: #10B981;
             font-size: 20px;
           }
           
           .error-icon {
             color: #EF4444;
             font-size: 20px;
           }
           
           .toast-content {
             flex: 1;
             font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
           }
           
           .toast-title {
             font-weight: 600;
             margin-bottom: 2px;
             color: ${toastNotification.type === 'success' ? '#065f46' : '#991b1b'};
           }
           
           .toast-message {
             font-size: 14px;
             color: ${toastNotification.type === 'success' ? '#065f46' : '#991b1b'};
             opacity: 0.9;
           }
           
           .toast-close {
             background: transparent;
             border: none;
             cursor: pointer;
             padding: 4px;
             margin-left: 8px;
             border-radius: 50%;
             display: flex;
             align-items: center;
             justify-content: center;
             opacity: 0.6;
             transition: opacity 0.2s ease-in-out;
           }
           
           .toast-close:hover {
             opacity: 1;
           }
           
           .close-icon {
             font-size: 12px;
             color: ${toastNotification.type === 'success' ? '#065f46' : '#991b1b'};
           }
           
           @keyframes slideIn {
             0% {
               transform: translateX(100%);
               opacity: 0;
             }
             100% {
               transform: translateX(0);
               opacity: 1;
             }
           }
           
           @keyframes slideOut {
             0% {
               transform: translateX(0);
               opacity: 1;
             }
             100% {
               transform: translateX(100%);
               opacity: 0;
             }
           }
         `}</style>
       </div>
     )}
     
     <style jsx>{`
       .cs-shares-container {
         --primary: #e74c3c;
         --primary-light: #ec7063;
         --primary-dark: #c0392b;
         --secondary: #2563EB;
         --secondary-dark: #1d4ed8;
         --accent: #10b981;
         --accent-dark: #059669;
         --dark-bg: #0a0f1e;
         --card-bg: #141b33;
         --input-bg: #1a2242;
         --border-color: rgba(231, 76, 60, 0.2);
         --success: #10b981;
         --error: #ef4444;
         --text-primary: #ffffff;
         --text-secondary: #94a3b8;
         --text-muted: #64748b;
         --animation-speed: 0.3s;
         
         background-color: var(--dark-bg);
         color: var(--text-primary);
         min-height: 100vh;
         padding-bottom: 50px;
         font-family: 'Inter', system-ui, sans-serif;
         position: relative;
       }
       
       /* Header Styles */
       .cs-shares-header {
         background: linear-gradient(135deg, var(--card-bg), #192145);
         padding: 24px 16px;
         display: flex;
         justify-content: space-between;
         align-items: center;
         position: relative;
         overflow: hidden;
       }
       
       .cs-shares-header::after {
         content: '';
         position: absolute;
         bottom: 0;
         left: 0;
         width: 100%;
         height: 40px;
         background: linear-gradient(to bottom right, transparent 0%, transparent 50%, var(--dark-bg) 50%, var(--dark-bg) 100%);
       }
       
       .cs-header-content {
         position: relative;
         z-index: 2;
       }
       
       .cs-shares-title {
         font-size: 24px;
         font-weight: 700;
         margin: 0 0 4px;
         background: linear-gradient(to right, var(--primary-light), var(--primary));
         -webkit-background-clip: text;
         -webkit-text-fill-color: transparent;
       }
       
       .cs-shares-subtitle {
         color: var(--text-secondary);
         font-size: 14px;
         margin: 0;
       }
       
       .cs-header-actions {
         position: relative;
         z-index: 2;
       }
       
       .cs-fund-button {
         background: var(--primary);
         color: white;
         border: none;
         border-radius: 12px;
         padding: 12px 16px;
         font-size: 14px;
         font-weight: 600;
         display: flex;
         align-items: center;
         gap: 8px;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
         box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
       }
       
       .cs-fund-button:hover {
         background: var(--primary-light);
         transform: translateY(-2px);
         box-shadow: 0 6px 16px rgba(231, 76, 60, 0.4);
       }
       
       .cs-fund-icon {
         font-size: 16px;
       }
       
       /* Shares Grid */
       .cs-shares-grid {
         display: grid;
         grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
         gap: 14px;
         padding: 20px 16px 0;
       }
       
       .cs-share-card {
         background: var(--card-bg);
         border-radius: 16px;
         overflow: hidden;
         box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
         border: 1px solid var(--border-color);
         transition: transform var(--animation-speed) ease, box-shadow var(--animation-speed) ease;
         position: relative;
       }
       
       .cs-share-card:hover {
         transform: translateY(-4px);
         box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
       }
       
       .cs-card-header {
         padding: 16px;
         display: flex;
         align-items: center;
         gap: 12px;
         border-bottom: 1px solid rgba(255, 255, 255, 0.05);
       }
       
       .cs-company-image {
         width: 48px;
         height: 48px;
         border-radius: 12px;
         overflow: hidden;
         flex-shrink: 0;
         background: rgba(255, 255, 255, 0.05);
       }
       
       .cs-company-logo {
         width: 100%;
         height: 100%;
         object-fit: cover;
       }
       
       .cs-company-info {
         flex: 1;
         display: flex;
         justify-content: space-between;
         align-items: center;
       }
       
       .cs-company-name {
         font-size: 16px;
         font-weight: 600;
         margin: 0;
         color: var(--text-primary);
         flex: 1;
       }
       
       .cs-company-icon {
         font-size: 20px;
         margin-left: 8px;
       }
       
       .cs-share-metrics {
         padding: 12px 16px;
       }
       
       .cs-metric-row {
         display: flex;
         gap: 12px;
         margin-bottom: 8px;
       }
       
       .cs-metric-row:last-child {
         margin-bottom: 0;
       }
       
       .cs-metric {
         flex: 1;
         display: flex;
         flex-direction: column;
         gap: 2px;
       }
       
       .cs-metric-label {
         font-size: 11px;
         color: var(--text-secondary);
         text-transform: uppercase;
         letter-spacing: 0.5px;
       }
       
       .cs-metric-value {
         font-size: 13px;
         font-weight: 600;
         color: var(--text-primary);
       }
       
       .cs-share-footer {
         padding: 16px;
         border-top: 1px solid rgba(255, 255, 255, 0.05);
         display: flex;
         justify-content: space-between;
         align-items: center;
       }
       
       .cs-share-price {
         display: flex;
         flex-direction: column;
         gap: 2px;
       }
       
       .cs-price-label {
         font-size: 11px;
         color: var(--text-secondary);
         text-transform: uppercase;
         letter-spacing: 0.5px;
       }
       
       .cs-price-value {
         font-size: 16px;
         font-weight: 700;
         color: var(--primary);
       }
       
       .cs-invest-button {
         color: white;
         border: none;
         border-radius: 10px;
         padding: 10px 16px;
         font-size: 13px;
         font-weight: 600;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
         min-width: 70px;
       }
       
       .cs-invest-button:hover:not(.cs-disabled) {
         transform: translateY(-1px);
         box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
       }
       
       .cs-invest-button.cs-disabled {
         background: var(--text-muted) !important;
         cursor: not-allowed;
       }
       
       /* Success View Styles */
       .cs-success-view {
         padding: 30px 16px;
         display: flex;
         flex-direction: column;
         align-items: center;
         text-align: center;
         max-width: 500px;
         margin: 0 auto;
       }
       
       .cs-success-animation {
         width: 80px;
         height: 80px;
         border-radius: 50%;
         background-color: rgba(16, 185, 129, 0.1);
         display: flex;
         align-items: center;
         justify-content: center;
         margin-bottom: 24px;
         animation: pulse 2s infinite;
       }
       
       @keyframes pulse {
         0% {
           box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
         }
         70% {
           box-shadow: 0 0 0 15px rgba(16, 185, 129, 0);
         }
         100% {
           box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
         }
       }
       
       .cs-success-icon {
         font-size: 40px;
         color: var(--success);
       }
       
       .cs-success-title {
         font-size: 24px;
         font-weight: 700;
         margin: 0 0 8px;
         color: var(--text-primary);
       }
       
       .cs-success-subtitle {
         font-size: 16px;
         color: var(--text-secondary);
         margin: 0 0 24px;
       }
       
       .cs-success-card {
         background: var(--card-bg);
         border-radius: 16px;
         border: 1px solid var(--border-color);
         width: 100%;
         overflow: hidden;
         margin-bottom: 20px;
       }
       
       .cs-success-header {
         padding: 16px;
         background: rgba(20, 27, 51, 0.4);
         display: flex;
         align-items: center;
         gap: 12px;
       }
       
       .cs-company-icon-small {
         width: 36px;
         height: 36px;
         border-radius: 8px;
         background: rgba(255, 255, 255, 0.1);
         display: flex;
         align-items: center;
         justify-content: center;
         font-size: 18px;
       }
       
       .cs-success-company-name {
         font-size: 16px;
         font-weight: 600;
         margin: 0;
         color: var(--text-primary);
       }
       
       .cs-success-details {
         padding: 16px;
       }
       
       .cs-success-detail {
         display: flex;
         justify-content: space-between;
         align-items: center;
         padding: 10px 0;
         border-bottom: 1px solid rgba(255, 255, 255, 0.05);
       }
       
       .cs-success-detail:last-child {
         border-bottom: none;
       }
       
       .cs-success-label {
         font-size: 14px;
         color: var(--text-secondary);
       }
       
       .cs-success-value {
         font-size: 14px;
         font-weight: 600;
         color: var(--text-primary);
       }
       
       .cs-status-complete {
         color: var(--success);
       }
       
       .cs-updated-balance {
         color: var(--primary);
       }
       
       .cs-info-card {
         background: rgba(20, 27, 51, 0.4);
         border-radius: 16px;
         padding: 16px;
         display: flex;
         flex-direction: column;
         align-items: center;
         width: 100%;
         margin-bottom: 24px;
       }
       
       .cs-info-icon {
         font-size: 24px;
         color: var(--primary);
         margin-bottom: 12px;
       }
       
       .cs-info-title {
         font-size: 16px;
         font-weight: 600;
         margin: 0 0 8px;
         color: var(--text-primary);
       }
       
       .cs-info-text {
         font-size: 14px;
         color: var(--text-secondary);
         margin: 0;
         text-align: center;
         line-height: 1.5;
       }
       
       .cs-success-actions {
         display: flex;
         flex-direction: column;
         gap: 12px;
         width: 100%;
       }
       
       .cs-view-button {
         background: var(--primary);
         color: white;
         border: none;
         border-radius: 12px;
         padding: 16px;
         font-size: 16px;
         font-weight: 600;
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 10px;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
       }
       
       .cs-view-button:hover {
         background: var(--primary-light);
         transform: translateY(-2px);
       }
       
       .cs-button-icon {
         font-size: 18px;
       }
       
       .cs-button-icon-right {
         margin-left: 4px;
         font-size: 16px;
       }
       
       .cs-home-button {
         background: transparent;
         color: var(--text-primary);
         border: 1px solid rgba(255, 255, 255, 0.1);
         border-radius: 12px;
         padding: 14px;
         font-size: 15px;
         font-weight: 500;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
       }
       
       .cs-home-button:hover {
         background: rgba(255, 255, 255, 0.05);
       }
       
       /* Form View Styles */
       .cs-form-view {
         max-width: 500px;
         margin: 0 auto;
         padding: 0 16px;
       }
       
       .cs-form-header {
         display: flex;
         align-items: center;
         gap: 16px;
         padding: 16px 0;
         margin-bottom: 16px;
       }
       
       .cs-back-button {
         width: 36px;
         height: 36px;
         border-radius: 50%;
         background: rgba(20, 27, 51, 0.4);
         color: var(--text-primary);
         border: none;
         display: flex;
         align-items: center;
         justify-content: center;
         font-size: 16px;
         cursor: pointer;
         transition: background-color var(--animation-speed) ease;
       }
       
       .cs-back-button:hover {
         background: rgba(20, 27, 51, 0.6);
       }
       
       .cs-form-title {
         font-size: 20px;
         font-weight: 600;
         margin: 0;
         color: var(--text-primary);
       }
       
       .cs-form-container {
         background: var(--card-bg);
         border-radius: 16px;
         border: 1px solid var(--border-color);
         overflow: hidden;
       }
       
       .cs-form-content {
         padding: 20px;
       }
       
       .cs-input-group {
         margin-bottom: 20px;
       }
       
       .cs-input-label {
         font-size: 14px;
         color: var(--text-primary);
         margin-bottom: 8px;
         display: block;
       }
       
       .cs-input-wrapper {
         position: relative;
       }
       
       .cs-input-field {
         width: 80%;
         background: var(--input-bg);
         border: 1px solid rgba(255, 255, 255, 0.1);
         border-radius: 12px;
         padding: 14px 16px;
         color: var(--text-primary);
         font-size: 16px;
         transition: border-color var(--animation-speed) ease;
       }
       
       .cs-input-field:focus {
         outline: none;
         border-color: var(--primary);
       }
       
       .cs-input-hint {
         font-size: 12px;
         color: var(--text-secondary);
         margin: 6px 0 0;
       }
       
       .cs-submit-button {
         width: 100%;
         background: var(--primary);
         color: white;
         border: none;
         border-radius: 12px;
         padding: 16px;
         font-size: 16px;
         font-weight: 600;
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 10px;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
       }
       
       .cs-submit-button:hover:not(.cs-disabled) {
         background: var(--primary-light);
         transform: translateY(-2px);
       }
       
       .cs-submit-button.cs-disabled {
         background: var(--text-muted);
         cursor: not-allowed;
       }
       
       .cs-payment-summary {
         padding: 20px;
         border-bottom: 1px solid rgba(255, 255, 255, 0.05);
       }
       
       .cs-summary-item {
         display: flex;
         justify-content: space-between;
         align-items: center;
         margin-bottom: 12px;
       }
       
       .cs-summary-item:last-child {
         margin-bottom: 0;
       }
       
       .cs-summary-label {
         font-size: 14px;
         color: var(--text-secondary);
       }
       
       .cs-summary-value {
         font-size: 14px;
         font-weight: 600;
         color: var(--text-primary);
       }
       
       .cs-payment-note {
         padding: 16px 20px;
         border-bottom: 1px solid rgba(255, 255, 255, 0.05);
       }
       
       .cs-payment-note p {
         font-size: 14px;
         color: var(--text-secondary);
         margin: 0;
         line-height: 1.5;
       }
       
       .cs-loader-container {
         display: flex;
         flex-direction: column;
         align-items: center;
         padding: 40px 20px;
         text-align: center;
       }
       
       .cs-loader {
         width: 50px;
         height: 50px;
         border: 4px solid rgba(255, 255, 255, 0.1);
         border-radius: 50%;
         border-top-color: var(--primary);
         animation: spin 1s linear infinite;
         margin-bottom: 20px;
       }
       
       @keyframes spin {
         0% { transform: rotate(0deg); }
         100% { transform: rotate(360deg); }
       }
       
       .cs-loader-title {
         font-size: 18px;
         font-weight: 600;
         margin: 0 0 8px;
         color: var(--text-primary);
       }
       
       .cs-loader-message {
         font-size: 14px;
         color: var(--text-secondary);
         margin: 0;
       }
       
       .cs-payment-error {
         display: flex;
         flex-direction: column;
         align-items: center;
         padding: 30px 20px;
         text-align: center;
       }
       
       .cs-error-icon {
         font-size: 30px;
         color: var(--error);
         margin-bottom: 16px;
       }
       
       .cs-error-title {
         font-size: 18px;
         font-weight: 600;
         margin: 0 0 8px;
         color: var(--error);
       }
       
       .cs-error-message {
         font-size: 14px;
         color: var(--text-secondary);
         margin: 0 0 20px;
         line-height: 1.5;
       }
       
       .cs-retry-button {
         background: var(--card-bg);
         color: var(--text-primary);
         border: 1px solid rgba(255, 255, 255, 0.1);
         border-radius: 12px;
         padding: 14px 24px;
         font-size: 15px;
         font-weight: 500;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
       }
       
       .cs-retry-button:hover {
         background: rgba(255, 255, 255, 0.05);
       }
       
       /* Modal Styles */
       .cs-modal-overlay {
         position: fixed;
         top: 0;
         left: 0;
         right: 0;
         bottom: 0;
         background: rgba(0, 0, 0, 0.8);
         display: flex;
         align-items: center;
         justify-content: center;
         z-index: 1000;
         padding: 16px;
         backdrop-filter: blur(5px);
         margin-bottom: 4rem;
       }
       
       .cs-modal-container {
         background: var(--card-bg);
         border-radius: 16px;
         width: 100%;
         max-width: 450px;
         box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
         border: 1px solid var(--border-color);
         overflow: hidden;
         animation: modalFadeIn 0.3s ease-out;
       }
       
       @keyframes modalFadeIn {
         from { opacity: 0; transform: translateY(20px); }
         to { opacity: 1; transform: translateY(0); }
       }
       
       .cs-modal-header {
         padding: 16px 20px;
         display: flex;
         justify-content: space-between;
         align-items: center;
         border-bottom: 1px solid rgba(255, 255, 255, 0.05);
       }
       
       .cs-modal-title {
         font-size: 18px;
         font-weight: 600;
         margin: 0;
         color: var(--text-primary);
       }
       
       .cs-modal-close {
         background: transparent;
         border: none;
         color: var(--text-secondary);
         font-size: 18px;
         cursor: pointer;
         display: flex;
         align-items: center;
         justify-content: center;
         padding: 4px;
         transition: color var(--animation-speed) ease;
       }
       
       .cs-modal-close:hover {
         color: var(--text-primary);
       }
       
       .cs-modal-content {
         padding: 20px;
       }
       
       .cs-company-highlight {
         display: flex;
         align-items: center;
         gap: 16px;
         margin-bottom: 20px;
         padding-bottom: 20px;
         border-bottom: 1px solid rgba(255, 255, 255, 0.05);
       }
       
       .cs-highlight-icon {
         width: 48px;
         height: 48px;
         border-radius: 12px;
         background: rgba(255, 255, 255, 0.1);
         display: flex;
         align-items: center;
         justify-content: center;
         font-size: 24px;
       }
       
       .cs-highlight-name {
         font-size: 18px;
         font-weight: 600;
         margin: 0;
         color: var(--text-primary);
       }
       
       .cs-quantity-selector {
         margin-bottom: 20px;
       }
       
       .cs-selector-label {
         font-size: 15px;
         font-weight: 600;
         margin: 0 0 12px;
         color: var(--text-primary);
       }
       
       .cs-selector-options {
         display: flex;
         flex-wrap: wrap;
         gap: 10px;
       }
       
       .cs-quantity-option {
         flex: 1;
         min-width: 60px;
         background: var(--input-bg);
         border: 1px solid rgba(255, 255, 255, 0.1);
         border-radius: 10px;
         padding: 12px 0;
         color: var(--text-primary);
         font-size: 15px;
         font-weight: 500;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
       }
       
       .cs-quantity-option:hover:not(.active) {
         background: rgba(255, 255, 255, 0.05);
       }
       
       .cs-quantity-option.active {
         background: var(--secondary);
         color: white;
         border-color: var(--secondary);
       }
       
       .cs-calculation-summary {
         background: var(--input-bg);
         border-radius: 12px;
         padding: 16px;
         margin-bottom: 20px;
       }
       
       .cs-calc-item {
         display: flex;
         justify-content: space-between;
         align-items: center;
         margin-bottom: 12px;
         padding-bottom: 12px;
         border-bottom: 1px solid rgba(255, 255, 255, 0.05);
       }
       
       .cs-calc-label {
         display: flex;
         align-items: center;
         gap: 8px;
         font-size: 14px;
         color: var(--text-secondary);
       }
       
       .cs-calc-icon {
         font-size: 16px;
       }
       
       .cs-calc-value {
         font-size: 14px;
         font-weight: 600;
         color: var(--text-primary);
       }
       
       .cs-calc-total {
         display: flex;
         justify-content: space-between;
         align-items: center;
         padding-top: 12px;
       }
       
       .cs-total-label {
         font-size: 15px;
         font-weight: 600;
         color: var(--text-primary);
       }
       
       .cs-total-value {
         font-size: 18px;
         font-weight: 700;
         color: var(--primary);
       }
       
       .cs-modal-submit {
         width: 100%;
         background: var(--primary);
         color: white;
         border: none;
         border-radius: 12px;
         padding: 16px;
         font-size: 16px;
         font-weight: 600;
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 10px;
         cursor: pointer;
         transition: all var(--animation-speed) ease;
       }
       
       .cs-modal-submit:hover {
         background: var(--primary-light);
         transform: translateY(-2px);
       }
       
       /* Media Queries for Responsive Design */
       @media (max-width: 600px) {
         .cs-shares-title {
           font-size: 20px;
         }
         
         .cs-shares-grid {
           grid-template-columns: 1fr;
           gap: 12px;
         }
         
         .cs-shares-header {
           padding: 20px 16px;
           flex-direction: column;
           align-items: flex-start;
           gap: 12px;
         }
         
         .cs-header-actions {
           align-self: flex-end;
         }
         
         .cs-fund-button {
           padding: 10px 14px;
           font-size: 13px;
         }
         
         .cs-success-title {
           font-size: 20px;
         }
         
         .cs-success-animation {
           width: 70px;
           height: 70px;
         }
         
         .cs-success-icon {
           font-size: 34px;
         }
         
         .cs-modal-container {
           max-width: 100%;
         }
         
         .cs-selector-options {
           gap: 8px;
           }
         
         .cs-quantity-option {
           min-width: 50px;
           padding: 10px 0;
         }
       }
       
       @media (max-width: 400px) {
         .cs-shares-grid {
           grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
         }
         
         .cs-card-header {
           padding: 12px;
         }
         
         .cs-company-image {
           width: 40px;
           height: 40px;
         }
         
         .cs-company-name {
           font-size: 14px;
         }
         
         .cs-share-metrics {
           padding: 10px 12px;
         }
         
         .cs-metric-value {
           font-size: 12px;
         }
         
         .cs-share-footer {
           padding: 12px;
         }
         
         .cs-price-value {
           font-size: 14px;
         }
         
         .cs-invest-button {
           padding: 8px 12px;
           font-size: 12px;
         }
         
         .cs-shares-header {
           padding: 16px 12px;
         }
         
         .cs-fund-button {
           padding: 8px 12px;
           font-size: 12px;
         }
       }
     `}</style>
   </div>
 );
};

export default CompanyShares;